import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { Users2, Plus, ChevronRight, Loader2, Target, ChevronLeft } from "lucide-react";

export default function Groups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  // Mount-once: load groups + students on first render only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/groups", { name, student_ids: Array.from(picked) });
      toast.success("Group created");
      setOpen(false); setName(""); setPicked(new Set());
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div>
      <button data-testid="groups-back-btn" onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5">
        <ChevronLeft size={16} /> Dashboard
      </button>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Across all classes</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-slate-800 tracking-tight mt-1">Support Groups</h1>
        </div>
        <button data-testid="new-group-btn" onClick={() => setOpen(true)}
          className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition shadow-sm hover:shadow-md flex items-center gap-2">
          <Plus size={18} /> New Group
        </button>
      </div>

      {groups === null ? (
        <div className="flex justify-center py-20 text-slate-300"><Loader2 className="animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-[0_4px_24px_rgba(42,59,71,0.04)]">
          <div className="h-16 w-16 rounded-3xl bg-[#9A7AA0]/15 text-[#9A7AA0] flex items-center justify-center mx-auto"><Users2 size={30} /></div>
          <h3 className="font-['Outfit'] text-2xl font-bold text-slate-800 mt-5">No groups yet</h3>
          <p className="text-slate-500 mt-2 max-w-sm mx-auto">Create a learning-support group with students from any of your classes and set shared targets.</p>
          <button data-testid="empty-new-group-btn" onClick={() => setOpen(true)} className="mt-6 bg-[#4A5D6B] text-white rounded-full px-6 py-2.5 font-semibold font-['Outfit'] inline-flex items-center gap-2"><Plus size={18} /> Create a group</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map((g) => (
            <button key={g.id} data-testid={`group-card-${g.id}`} onClick={() => navigate(`/group/${g.id}`)}
              className="group text-left bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 hover:shadow-[0_8px_30px_rgba(42,59,71,0.09)] hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <span className="h-11 w-11 rounded-2xl bg-[#9A7AA0]/15 text-[#9A7AA0] flex items-center justify-center"><Users2 size={22} /></span>
                <ChevronRight size={20} className="text-slate-300 group-hover:text-[#9A7AA0] transition" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-slate-800 mt-4">{g.name}</h3>
              <div className="flex items-center gap-4 text-sm text-slate-400 mt-4 pt-4 border-t border-slate-50">
                <span className="flex items-center gap-1.5"><Users2 size={15} /> {g.member_count}</span>
                <span className="flex items-center gap-1.5"><Target size={15} /> {g.target_count} shared</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <Modal title="Create a support group" onClose={() => setOpen(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label="Group name">
              <input data-testid="group-name-input" required autoFocus value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Phonics withdrawal group"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A] focus:ring-2 focus:ring-[#81B29A]/40" />
            </Field>
            <div>
              <span className="text-sm font-semibold text-slate-600 font-['Outfit']">Add students ({picked.size} selected)</span>
              <div className="mt-2 max-h-56 overflow-y-auto space-y-1.5 border border-slate-100 rounded-xl p-2">
                {students.length === 0 && <p className="text-sm text-slate-400 p-2">No students yet — add students to a class first.</p>}
                {students.map((s) => (
                  <button type="button" key={s.id} data-testid={`pick-student-${s.id}`} onClick={() => toggle(s.id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${picked.has(s.id) ? "bg-[#81B29A]/15 text-slate-800" : "hover:bg-slate-50 text-slate-600"}`}>
                    <span className={`h-4 w-4 rounded border flex items-center justify-center ${picked.has(s.id) ? "bg-[#81B29A] border-[#81B29A]" : "border-slate-300"}`}>{picked.has(s.id) && <span className="text-white text-[10px]">✓</span>}</span>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="save-group-btn" disabled={saving} className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={18} className="animate-spin" />} Create group
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
